import { Navigate, Outlet } from 'react-router-dom';

import AccountControl from '@components/controlDetails/account/AccountControl';
import CustomerControl from '@components/controlDetails/customer/CustomerControl';
import Layout from '@components/layout/Layout';
import LoginRoute from '@components/routing/LoginRoute';
import ProtectedRoute from '@components/routing/ProtectedRoute';
import AccountContextRoute from '@pages/accountContainer/AccountContextRoute';
import AccountLayout, {
    HeaderType,
} from '@pages/accountContainer/AccountLayout';
import DetailLayout from '@pages/accountDetail/DetailLayout';
import AccountContainer from '@pages/accountDetail/tabs/account/AccountContainer';
import ControlsContainer from '@pages/accountDetail/tabs/controls/ControlsContainer';
import LocationsContainer from '@pages/accountDetail/tabs/locations/LocationsContainer';
import CustomersPhonesContainer from '@pages/accountDetail/tabs/phoneNumbers/CustomersPhonesContainer';
import UsersContainer from '@pages/accountDetail/tabs/users/UsersContainer';
import AccountHistory from '@pages/accountHistory/AccountHistory';
import AccountSearch from '@pages/accountSearch/AccountSearch';
import AccountStatements from '@pages/accountStatements/AccountStatements';
import AccountTimeline from '@pages/accountTimeline/AccountTimeline';
import TimelineDetailsContainer from '@pages/accountTimelineDetails/TimelineDetailsContainer';
import AccountTransactionDetails from '@pages/accountTransactionDetails/AccountTransactionDetails';
import Delinquency from '@pages/delinquency/Delinquency';
import CreateCardControlContainer from '@pages/flexControls/createCardControlContainer/CreateCardControlContainer';
import CreateCustomerControlContainer from '@pages/flexControls/createCustomerFlexControl/CreateCustomerControlContainer';
import CreateControlContainer from '@pages/flexControls/createFlexControl/CreateControlContainer';
import Limits from '@pages/limits/Limits';
import Overview from '@pages/overview/Overview';
import Transactions from '@pages/transactions/Transactions';

import SuspendedCardsApp from '../../frontends/cards/SuspendedCardsApp';
import SuspendedLendingApp from '../../frontends/lending/SuspendedLendingApp';

const routes = [
    {
        path: '/',
        element: (
            <ProtectedRoute>
                <Layout />
            </ProtectedRoute>
        ),
        children: [
            {
                index: true,
                element: <AccountSearch />,
            },
            {
                path: ':accountId',
                element: <AccountContextRoute />,
                children: [
                    {
                        element: (
                            <AccountLayout
                                headerType={HeaderType.ShortSticky}
                            />
                        ),
                        children: [
                            {
                                index: true,
                                element: <Navigate to="overview" replace />,
                            },
                            {
                                path: 'overview',
                                element: <Overview />,
                            },
                            {
                                path: 'lending/*',
                                element: (
                                    <SuspendedLendingApp basename="lending" />
                                ),
                            },
                            {
                                path: 'cards/*',
                                element: <SuspendedCardsApp />,
                            },
                            {
                                path: 'timeline',
                                children: [
                                    {
                                        index: true,
                                        element: <AccountTimeline />,
                                    },
                                    {
                                        path: ':timelineId',
                                        element: <TimelineDetailsContainer />,
                                    },
                                    {
                                        path: ':timelineId/nextcursor/:nextcursor',
                                        element: <TimelineDetailsContainer />,
                                    },
                                ],
                            },
                            {
                                path: 'statements',
                                element: <Outlet />,
                                children: [
                                    {
                                        index: true,
                                        element: <AccountStatements />,
                                    },
                                    {
                                        path: ':transactionId',
                                        element: <AccountTransactionDetails />,
                                    },
                                ],
                            },
                            {
                                path: 'customers/:customerId/limits',
                                element: <Limits />,
                            },

                            {
                                path: 'transactions',
                                element: <Outlet />,
                                children: [
                                    {
                                        index: true,
                                        element: <Transactions />,
                                    },
                                    {
                                        path: ':transactionId',
                                        element: <AccountTransactionDetails />,
                                    },
                                ],
                            },
                            {
                                path: 'customers/:customerId/history/*',
                                element: <AccountHistory />,
                            },
                            {
                                path: 'delinquency',
                                element: <Delinquency />,
                            },
                        ],
                    },
                    {
                        element: <AccountLayout headerType={HeaderType.Full} />,
                        children: [
                            {
                                element: <DetailLayout />,
                                children: [
                                    {
                                        path: 'detail',
                                        element: <AccountContainer />,
                                    },
                                    {
                                        path: 'detail/customers',
                                        element: <UsersContainer />,
                                    },
                                    {
                                        path: 'detail/controls',
                                        element: <ControlsContainer />,
                                    },
                                    {
                                        path: 'detail/phonenumbers',
                                        element: <CustomersPhonesContainer />,
                                    },
                                    {
                                        path: 'detail/addresses',
                                        element: <LocationsContainer />,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        element: <AccountLayout headerType={HeaderType.None} />,
                        children: [
                            {
                                path: 'detail/controls/flexcontrol/:flexControlId',
                                element: <AccountControl />,
                            },
                            {
                                path: 'detail/controls/customer/:customerId/flexcontrol/:flexControlId',
                                element: <CustomerControl />,
                            },
                        ],
                    },
                    {
                        element: <CreateControlContainer />,
                        path: 'flex-controls/account/create',
                    },
                    {
                        element: <CreateCustomerControlContainer />,
                        path: 'flex-controls/customers/:customerId/create',
                    },
                    {
                        element: <CreateCardControlContainer />,
                        path: 'flex-controls/customers/:customerId/card/create',
                    },
                ],
            },
        ],
    },
    {
        path: '*',
        element: <div>Error</div>,
    },
    ...(process.env.NODE_ENV === 'development'
        ? [
              {
                  path: '/login',
                  element: <LoginRoute />,
              },
          ]
        : []),
];

export default routes;
